import React, { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  MagnifyingGlass, House, Users, Package, CurrencyCircleDollar, Bank, ShoppingBag,
  ShoppingCart, Receipt, Buildings, HandCoins, Briefcase, ChatCircleDots, ChartBar,
  FileText, Gear, Scales, Lightbulb, CheckCircle, Info,
} from "@phosphor-icons/react";

/**
 * Panduan Pengguna AIAkuntan — step-by-step tiap menu.
 * Semua step disusun dari pengalaman produk, bukan screenshot statis;
 * tangkapan layar bisa dilampirkan belakangan dengan menaruh file di
 * /app/frontend/public/panduan/{slug}-{n}.png dan `imageUrl` di step.
 */

const SLUG_IMAGE = {
  dashboard: "/panduan/dashboard.jpg",
  partners: "/panduan/partners.jpg",
  inventory: "/panduan/inventory.jpg",
  sales: "/panduan/sales.jpg",
  purchases: "/panduan/purchases.jpg",
  cashbank: "/panduan/cashbank.jpg",
  accounting: "/panduan/accounting.jpg",
  "fund-requests": "/panduan/fund-requests.jpg",
  projects: "/panduan/projects.jpg",
  "fixed-assets": "/panduan/fixed-assets.jpg",
  payroll: "/panduan/payroll.jpg",
  ai: "/panduan/ai.jpg",
  reports: "/panduan/reports.jpg",
  tax: "/panduan/tax.jpg",
  approvals: "/panduan/approvals.jpg",
  team: "/panduan/team.jpg",
  settings: "/panduan/settings.jpg",
  superadmin: "/panduan/superadmin.jpg",
  // SLUG_IMAGE superadmin dipertahankan di map (file ada), tetapi SECTIONS superadmin dihapus
  // karena kontennya khusus akun Platform Admin — tidak relevan untuk pengguna biasa.
};

const SECTIONS = [
  {
    id: "dashboard", title: "Dashboard", icon: House, color: "amber",
    intro: "Ringkasan arus kas, laba/rugi berjalan, dan tagihan jatuh tempo. Panel ini adalah pos komando harian Anda — buka tiap pagi untuk cek kesehatan bisnis.",
    steps: [
      { title: "Cek kartu KPI utama", desc: "Kas, Piutang, Hutang, dan Laba/Rugi bulan berjalan tampil di atas. Klik kartu untuk lompat ke modul terkait." },
      { title: "Lihat Tagihan Jatuh Tempo", desc: "Daftar faktur yang akan jatuh tempo 7 hari ke depan. Klik baris untuk membuka faktur, lalu tekan 'Catat Pelunasan' bila sudah dibayar." },
      { title: "Prediksi Arus Kas 30 Hari", desc: "Grafik proyeksi berdasarkan tagihan masuk/keluar. Garis merah di bawah 0 = waspada kekurangan kas." },
      { title: "Widget AI CFO", desc: "Ringkasan mingguan dari AI: tren pendapatan, anomali beban, saran tindakan. Baca tiap Senin pagi untuk prioritas minggu ini." },
    ],
  },
  {
    id: "partners", title: "Pelanggan & Vendor", icon: Users, color: "sky",
    intro: "Master pelanggan (customer) dan pemasok (vendor). Data di sini dipakai di semua tahap penjualan & pembelian.",
    steps: [
      { title: "Tambah pelanggan/vendor", desc: "Klik Tambah → isi Nama (wajib), Tipe (customer / vendor / both), NPWP 16 digit, NITKU 22 digit (untuk e-Faktur), Termin Pembayaran (COD/14/30 hari), alamat, email, telepon → Simpan." },
      { title: "Impor massal via Excel", desc: "Klik Template → unduh file contoh. Isi data mulai baris 5. Kembali ke Partners → Impor Excel → pilih file. Baris duplikat (nama + NPWP sama) akan dilewati." },
      { title: "Edit saldo awal", desc: "Buka kartu → tab Saldo Awal → masukkan Piutang/Hutang awal + tanggal cutoff. Jurnal pembuka otomatis dibuat ke akun lawan 1200/2000." },
      { title: "Lihat riwayat transaksi", desc: "Klik kartu pelanggan → tab Riwayat untuk melihat semua faktur, pembayaran, dan saldo berjalan." },
    ],
  },
  {
    id: "inventory", title: "Barang & Jasa", icon: Package, color: "indigo",
    intro: "Master produk fisik (stok) dan jasa. Harga, HPP, akun pendapatan/beban, dan unit per item.",
    steps: [
      { title: "Buat produk baru", desc: "Tambah → SKU, Nama, Tipe (Produk/Jasa), Satuan, Harga Jual, HPP, Akun Pendapatan (default 4000), Akun HPP (default 5000), Stok Awal, Gudang → Simpan." },
      { title: "Kelola multi-gudang", desc: "Buka Gudang → Tambah lokasi. Pindah stok antar-gudang via Transfer Stok (tab Mutasi)." },
      { title: "Cetak label barcode", desc: "Pilih produk → tombol Cetak Label (format Tom & Jerry 121) — SKU + harga + barcode EAN-13." },
      { title: "Stock Opname berkala", desc: "Tab Stock Opname → Buat Sesi → scan/input quantity fisik. Sistem menampilkan selisih; klik Jurnalkan untuk auto-posting ke 1300/6000." },
    ],
  },
  {
    id: "sales", title: "Penjualan", icon: ShoppingBag, color: "emerald",
    intro: "6 tahap: Penawaran → Pesanan → Pengiriman → Faktur → Retur → Penerimaan. Setiap tahap otomatis bikin jurnal PSAK yang sesuai.",
    steps: [
      { title: "Buat Penawaran (Quotation)", desc: "Tab Penawaran → Baru → pilih Pelanggan (ketik nama/cari), isi tanggal, No. PO Pelanggan (opsional), tambah item + qty + harga. Simpan → kirim via Email (tombol pesawat kertas, bisa pilih 1 dari 5 template email)." },
      { title: "Konversi ke Pesanan (SO)", desc: "Buka Penawaran yg disetujui → tombol 'Konversi ke SO'. SO perlu Approve dulu (tombol Setujui) sebelum bisa jadi Pengiriman." },
      { title: "Buat Pengiriman (DO)", desc: "Dari SO approved → Konversi ke DO. Jurnal: DR 1310 Barang Terkirim | CR 1300 Persediaan (kliring; akan di-reverse saat faktur)." },
      { title: "Terbitkan Faktur", desc: "Dari DO → Konversi ke Faktur. Pilih Termin (COD/14/30/custom) → jatuh tempo auto-set. Jurnal: DR 1200 Piutang | CR 4000 Pendapatan + CR 2110 PPN Keluaran; sekaligus reverse 1310 → 5000 HPP." },
      { title: "Catat Pelunasan", desc: "Tab Penerimaan → Baru → pilih Faktur. Isi tanggal bayar + akun kas/bank → Simpan. Jurnal: DR 1100 Bank | CR 1200 Piutang." },
      { title: "Faktur Berulang (Recurring)", desc: "Menu Faktur Berulang → Template Baru → pola (harian/mingguan/bulanan) + CRON otomatis bikin draft tiap periode." },
    ],
  },
  {
    id: "purchases", title: "Pembelian", icon: ShoppingCart, color: "rose",
    intro: "6 tahap: Permintaan → Pesanan (PO) → Penerimaan (GRN) → Faktur Pembelian → Retur → Pembayaran.",
    steps: [
      { title: "Permintaan Pembelian (PR)", desc: "Tab Permintaan → Baru. Dipakai departemen untuk minta pembelian; perlu Approve manajer sebelum jadi PO." },
      { title: "Buat PO ke Vendor", desc: "Konversi PR → PO. Pilih Vendor (ketik nama), tanggal, item, harga nego. Kirim via Email (template dapat dipilih)." },
      { title: "Terima Barang (GRN)", desc: "Barang datang → tab Penerimaan → Baru dari PO → verifikasi qty aktual. Jurnal: DR 1300 Persediaan | CR 2200 Barang Diterima Belum Ditagih." },
      { title: "Catat Faktur Pembelian", desc: "Tagihan tiba → tab Faktur Beli → Baru dari GRN. Jurnal: reverse 2200 → DR 2200 | CR 2000 Hutang Dagang (+PPN Masukan 1500)." },
      { title: "Bayar Vendor", desc: "Tab Pembayaran → pilih faktur hutang → tanggal + akun bank. Jurnal: DR 2000 Hutang | CR 1100 Bank." },
    ],
  },
  {
    id: "cashbank", title: "Kas & Bank", icon: Bank, color: "amber",
    intro: "Transaksi tunai & bank yang tidak terkait faktur: beban operasional ad-hoc, transfer antar rekening, setoran modal.",
    steps: [
      { title: "Kas Masuk", desc: "Baru → pilih akun bank → kategori (Modal/Pinjaman/Lainnya) → akun lawan → tanggal + nominal → Simpan." },
      { title: "Kas Keluar", desc: "Baru → pilih bank → kategori beban (listrik, sewa, dll.) → akun lawan 6xxx → tanggal + nominal. Opsional: tautkan ke Proyek (ketik kode/nama)." },
      { title: "Transfer Antar Rekening", desc: "Tab Transfer → pilih bank asal + bank tujuan + nominal → tanggal. Dua jurnal otomatis dibuat tanpa menyentuh akun lawan." },
      { title: "Rekonsiliasi Bank", desc: "Menu Bank Reconciliation → upload CSV mutasi bank → AI Smart Match mencocokkan ≥95% transaksi otomatis. Sisanya cocokkan manual klik-tarik." },
    ],
  },
  {
    id: "accounting", title: "Akuntansi", icon: Receipt, color: "slate",
    intro: "Chart of Accounts PSAK, jurnal manual, dan ringkasan Laba Rugi + Neraca real-time.",
    steps: [
      { title: "Chart of Accounts (COA)", desc: "Tab Chart of Accounts → Tambah akun. Hierarki 4 digit: 1xxx Aset, 2xxx Liabilitas, 3xxx Ekuitas, 4xxx Pendapatan, 5xxx HPP, 6xxx Beban." },
      { title: "Buat Jurnal Manual", desc: "Tab Jurnal Umum → Baru → tanggal, referensi, tambahkan baris Debit/Kredit (saldo harus imbang). Tautkan Proyek (opsional) untuk masuk L/R proyek." },
      { title: "Lihat Buku Besar per akun", desc: "Menu Laporan (Buku Besar) → pilih akun → periode. Semua mutasi + saldo berjalan tampil + dapat diekspor Excel." },
      { title: "Cetak Laporan Keuangan", desc: "Tab Ringkasan → tombol Cetak Laporan Keuangan → PDF Laba Rugi + Neraca periode berjalan." },
    ],
  },
  {
    id: "fund-requests", title: "Pengajuan Dana", icon: HandCoins, color: "orange",
    intro: "Beban non-faktur yang perlu persetujuan sebelum keluarkan kas (uang muka proyek, reimbursement, operasional ad-hoc).",
    steps: [
      { title: "Ajukan dana", desc: "Baru → tanggal → tambahkan item (deskripsi, akun beban 6xxx, nominal, opsional tautkan ke Proyek) → kirim ke Approval." },
      { title: "Approval", desc: "Approver menerima notifikasi di Approval Center (atau WhatsApp bila diaktifkan). Klik Setuju / Tolak." },
      { title: "Eksekusi (bayar)", desc: "Setelah approved → buka pengajuan → tombol Eksekusi → pilih akun bank. Jurnal: DR 6xxx Beban | CR 1100 Bank — langsung masuk L/R proyek bila ditautkan." },
    ],
  },
  {
    id: "projects", title: "Proyek", icon: Briefcase, color: "indigo",
    intro: "Pelacakan L/R per proyek: pendapatan dari faktur yang ditautkan - HPP + beban (fund request / jurnal bertag proyek).",
    steps: [
      { title: "Buat Proyek", desc: "Baru → Kode (PRJ-2026-001), Nama, Pelanggan, tanggal mulai/selesai, Budget → Simpan." },
      { title: "Impor massal", desc: "Tombol Template → unduh. Isi (code, name, customer_name, start_date, end_date, budget, status, manager). Impor → baris duplikat/kosong dilewati." },
      { title: "Tautkan transaksi", desc: "Saat buat Faktur/Pengajuan Dana/Kas Keluar/Jurnal, pilih proyek via combobox (ketik kode/nama)." },
      { title: "Review L/R per proyek", desc: "Klik baris proyek → dialog detail. Kartu 'Laba/Rugi Estimasi' (dari dokumen draft) + 'Laba/Rugi Real' (dari dokumen tereksekusi) tampil di atas, lengkap batch transaksi + daftar dokumen." },
    ],
  },
  {
    id: "fixed-assets", title: "Aktiva Tetap", icon: Buildings, color: "slate",
    intro: "Register aset tetap (kendaraan, mesin, bangunan) dengan penyusutan garis lurus otomatis bulanan.",
    steps: [
      { title: "Catat aset", desc: "Baru → Nama, kategori, tanggal perolehan, harga perolehan, umur ekonomis (bulan), nilai residu, akun aset 1600 + akun akumulasi 1650." },
      { title: "Jalankan Penyusutan Bulanan", desc: "Tombol Jalankan Penyusutan (CRON otomatis setiap akhir bulan). Jurnal: DR 6900 Beban Penyusutan | CR 1650 Akumulasi." },
      { title: "Pelepasan (sold / written-off)", desc: "Buka aset → tombol Lepas → tanggal + nilai jual. Jurnal pelepasan + L/R pelepasan otomatis." },
    ],
  },
  {
    id: "payroll", title: "Penggajian", icon: Users, color: "emerald",
    intro: "Gaji karyawan + perhitungan PPh 21 TER + BPJS Kesehatan/Ketenagakerjaan sesuai aturan 2024+.",
    steps: [
      { title: "Master Karyawan", desc: "Tab Karyawan → Tambah → NIK, status PTKP (TK/K0/K1/dst.), NPWP, gaji pokok, tunjangan, BPJS." },
      { title: "Komponen Gaji", desc: "Tab Komponen → tambah komponen (lembur, bonus, potongan) + apakah kena pajak + jenis (tunjangan/tunjangan PPh ditanggung perusahaan)." },
      { title: "Jalankan Payroll Bulanan", desc: "Tab Run Payroll → periode → Hitung → sistem menghitung PPh 21 TER + BPJS + take-home. Review → Approve → Posting Jurnal → payslip PDF per karyawan." },
    ],
  },
  {
    id: "ai", title: "AI & Integrasi", icon: ChatCircleDots, color: "amber",
    intro: "AI Bookkeeper (chat), Telegram Bot, WhatsApp Bot — otomatisasi pencatatan dari pesan/suara/foto struk.",
    steps: [
      { title: "AI Bookkeeper (Chat)", desc: "Menu AI → ketik 'Catat kas keluar listrik Rp 350.000 kemarin' → AI balas konfirmasi + draft jurnal. Approve → tercatat." },
      { title: "Telegram Bot", desc: "Menu Telegram → Hubungkan Bot (ikuti instruksi BotFather) → Mulai chat di Telegram → kirim pesan/foto struk; AI OCR + parse + konfirmasi." },
      { title: "WhatsApp Bot", desc: "Menu WhatsApp (paket Business+) → Connect via Fonnte → kirim voice note / foto struk / teks ke nomor bot → AI proses + minta approve via tombol WA." },
    ],
  },
  {
    id: "reports", title: "Laporan", icon: ChartBar, color: "indigo",
    intro: "Laporan standar (Laba Rugi, Neraca, Buku Besar, Arus Kas) + Custom Report Builder + Laporan Lanjutan (per proyek, umur piutang).",
    steps: [
      { title: "Laporan Standar", desc: "Menu Laporan → pilih jenis + periode (filter tanggal Dari/Sampai) → Lihat. Semua laporan bisa diekspor PDF & Excel." },
      { title: "Custom Report Builder", desc: "Menu Custom Reports → Baru → pilih sumber (jurnal, faktur, dsb.) + kolom + filter + grup → Simpan sebagai template → bisa dijalankan ulang kapan saja." },
      { title: "Laporan Lanjutan", desc: "Menu Advanced Reports → L/R per Proyek, Umur Piutang, Pajak PPN/PPh ringkasan → ekspor Excel/PDF." },
    ],
  },
  {
    id: "tax", title: "Pajak (PPN & PPh)", icon: Scales, color: "rose",
    intro: "e-Faktur PPN (XML CoreTax) + e-Bupot PPh (XML DJP). Export siap impor ke sistem pajak resmi.",
    steps: [
      { title: "Setup Pajak", desc: "Pengaturan → tab Pajak → isi NPWP 16 digit, NITKU 22 digit, nama perusahaan, kode transaksi default (01-10). Isi juga NITKU 22 digit di kartu Master Pelanggan." },
      { title: "Terbitkan Faktur dengan PPN", desc: "Buat Faktur Penjualan → pilih regulasi pajak PPN 12% (atau sesuai) di item. Jurnal PPN Keluaran 2110 otomatis." },
      { title: "Export XML CoreTax", desc: "Buka Faktur → tombol XML. Unduh XML, impor ke portal CoreTax DJP. Nomor seri faktur pajak diisi setelah terbit di CoreTax, lalu update kembali di sistem." },
      { title: "e-Bupot PPh", desc: "Menu e-Bupot → buat bukti potong (PPh 23/26/4(2)) → export XML → impor ke e-Bupot DJP." },
    ],
  },
  {
    id: "approvals", title: "Approval Center", icon: CheckCircle, color: "emerald",
    intro: "Semua permintaan persetujuan (SO, PO, Pengajuan Dana, Payroll) terpusat di satu antrian — dengan rute per peran.",
    steps: [
      { title: "Konfigurasi rute approval", desc: "Pengaturan → tab Approval Workflow → tentukan role approver per jenis dokumen + ambang nominal (opsional)." },
      { title: "Antrian saya", desc: "Menu Approval Center → tab 'Perlu Saya Approve' → klik baris → review → Setuju / Tolak (opsional isi komentar)." },
      { title: "Approval via WhatsApp", desc: "Bila WhatsApp Bot aktif, approver dapat tombol Setuju / Tolak langsung dari pesan WA — tidak perlu buka aplikasi." },
    ],
  },
  {
    id: "team", title: "Tim & Peran", icon: Users, color: "slate",
    intro: "Undang anggota tim + kontrol akses granular per modul via roles.",
    steps: [
      { title: "Undang anggota", desc: "Menu Tim → Undang → email + peran default → kirim. Anggota dapat link email untuk accept invite + set password." },
      { title: "Kelola Peran", desc: "Menu Roles → Buat peran (mis. Kasir, Manajer Gudang) → pilih modul + aksi (view/create/edit/delete/approve) → Simpan." },
      { title: "Assign peran", desc: "Menu Tim → klik anggota → dropdown Peran. Satu user bisa punya multi-role (gabungan izin)." },
    ],
  },
  {
    id: "settings", title: "Pengaturan", icon: Gear, color: "slate",
    intro: "Konfigurasi tenant tersusun dalam 8 tab: Profil Perusahaan, Umum, Pajak (e-Faktur), Gudang, Nomor Otomatis, Keamanan, Integrasi (Email), dan Job Desk AI. Atur sesuai kebutuhan sebelum mulai mencatat transaksi.",
    steps: [
      { title: "Tab Profil Perusahaan", desc: "Nama, alamat, logo (upload — tampil di kop surat PDF), NPWP, telepon. Di sini juga pengaturan layout PDF: ukuran kertas (A4/A5) dan orientasi (Potrait/Landscape), bisa berbeda per jenis dokumen." },
      { title: "Tab Umum", desc: "Informasi workspace (nama, paket, mata uang), kartu Akun Saya (nama, email, peran), dan yang penting: Metode Penilaian Persediaan — pilih Rata-Rata Tertimbang (AVERAGE) atau FIFO. Metode ini menentukan cara HPP dihitung saat pembelian & penjualan dan berlaku untuk seluruh gudang." },
      { title: "Tab Pajak (e-Faktur)", desc: "Isi identitas pajak untuk XML CoreTax & e-Bupot: Nama Badan (sesuai NPWP), NPWP 16 digit, NITKU/IDTKU 22 digit (dipakai sebagai SellerIDTKU di XML), Kode Transaksi Default (01 s/d 09), Tarif PPN Default (%), dan Nama Penandatangan. Jangan lupa klik Simpan Pengaturan Pajak." },
      { title: "Tab Gudang", desc: "Kelola multi-gudang: Gudang Baru → isi Kode (MAIN, JKT, SBY…), Nama, Alamat, Penanggung Jawab, dan centang 'Jadikan Gudang Default' bila perlu. Gudang MAIN tidak dapat dihapus. Tabel bisa diekspor ke Excel." },
      { title: "Tab Nomor Otomatis", desc: "Atur format penomoran tiap jenis dokumen (Penawaran, SO, Faktur, PO, Jurnal, dll.): klik Edit → Prefix, Sertakan Tahun (YYYY/YY), Sertakan Bulan, Sertakan Tanggal (DD), Reset Sequence (tidak pernah/tahunan/bulanan/harian), Padding Digit, Nomor Awal. Contoh nomor tampil real-time sebelum disimpan." },
      { title: "Tab Keamanan", desc: "Ganti password akun Anda: masukkan password lama, password baru (minimal 8 karakter), konfirmasi → Simpan. Segera lakukan logout-login ulang setelah penggantian." },
      { title: "Tab Integrasi (Email)", desc: "Hubungkan kanal email untuk kirim faktur & pengingat: (a) Gmail OAuth — klik Hubungkan Google lalu izinkan; (b) SMTP alternatif tanpa Google Cloud Console — untuk Gmail buat App Password 16 karakter di myaccount.google.com/apppasswords, atau isi host/port SMTP lain (Zoho/Outlook/domain sendiri) lalu 'Kirim Email Tes'. Di bawahnya ada Pengingat Jatuh Tempo Otomatis: aktifkan, pilih H-berapa sebelum jatuh tempo (H-1 s/d H-7) dan user pengirim — sistem mengirim email pengingat tiap hari 08:00 WIB ke faktur belum lunas." },
      { title: "Tab Job Desk AI", desc: "Atur pekerjaan rutin otomatis berbasis AI per peran (mis. Admin vs Accounting): ringkasan harian/mingguan yang dibuat via CRON — pilih jadwal, penerima, dan jenis laporan yang ingin dikirim otomatis via email/WhatsApp." },
    ],
  },
];

export default function Panduan() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState("dashboard");
  const filtered = useMemo(() => {
    if (!q.trim()) return SECTIONS;
    const kw = q.toLowerCase();
    return SECTIONS.filter((s) =>
      s.title.toLowerCase().includes(kw) ||
      s.intro.toLowerCase().includes(kw) ||
      s.steps.some((st) => (st.title + st.desc).toLowerCase().includes(kw)));
  }, [q]);
  const active = filtered.find((s) => s.id === open) || filtered[0];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="panduan-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-amber-600">Dokumentasi</div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900">Panduan Pengguna</h1>
        <p className="text-sm text-slate-500 mt-1">Alur operasional tiap menu — baca sekilas sebelum mulai, atau cari langkah tertentu lewat kotak pencarian.</p>
      </div>

      <div className="relative mb-5 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari modul atau langkah…" className="pl-9" data-testid="panduan-search" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <nav className="space-y-1" data-testid="panduan-nav">
          {filtered.map((s) => {
            const Icon = s.icon;
            const active = open === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setOpen(s.id)}
                data-testid={`panduan-nav-${s.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left transition-colors ${active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <Icon size={16} weight={active ? "fill" : "regular"} />
                <span className="flex-1 truncate">{s.title}</span>
                <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${active ? "border-white/30 text-white/90" : "border-slate-200 text-slate-500"}`}>{s.steps.length}</Badge>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="text-xs text-slate-400 italic p-3">Tidak ada hasil untuk "{q}"</div>}
        </nav>

        {active && (
          <article className="space-y-5" data-testid={`panduan-section-${active.id}`}>
            <header className="pb-3 border-b border-slate-200">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-600">
                <active.icon size={14} weight="fill" />
                <span>Modul</span>
              </div>
              <h2 className="font-serif-display text-3xl text-slate-900 mt-1">{active.title}</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{active.intro}</p>
              {SLUG_IMAGE[active.id] && (
                <img
                  src={SLUG_IMAGE[active.id]}
                  alt={`Tampilan menu ${active.title}`}
                  loading="lazy"
                  className="mt-4 rounded-lg border border-slate-200 shadow-sm w-full max-w-3xl"
                  data-testid={`panduan-img-${active.id}`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
            </header>

            <ol className="space-y-4" data-testid="panduan-steps">
              {active.steps.map((st, idx) => (
                <li key={idx} className="relative pl-10 py-3 pr-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`panduan-step-${idx}`}>
                  <span className="absolute left-3 top-3 h-6 w-6 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center tabular-nums">{idx + 1}</span>
                  <div className="font-medium text-slate-900 text-sm">{st.title}</div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{st.desc}</p>
                  {st.imageUrl && (
                    <img src={st.imageUrl} alt={st.title} className="mt-3 rounded border border-slate-200 max-w-full"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                </li>
              ))}
            </ol>

            <aside className="flex gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50">
              <Lightbulb size={18} className="shrink-0 text-amber-600" weight="fill" />
              <div className="text-xs text-amber-900 leading-relaxed">
                <strong>Butuh ilustrasi lebih detail?</strong> Tangkapan layar hero di atas adalah tampilan utama modul. Untuk screenshot per-langkah, letakkan file di <code className="font-mono-plex bg-amber-100 px-1 rounded">/public/panduan/{active.id}-{"{nomor-langkah}"}.jpg</code> dan tambahkan <code>imageUrl</code> di tiap step di <code className="font-mono-plex bg-amber-100 px-1 rounded">Panduan.jsx</code>.
              </div>
            </aside>
          </article>
        )}
      </div>
    </div>
  );
}
